/**
 * Settings > Notifications > one message type
 * (/settings/notifications/messages/:eventKey, ADMIN only like the parent
 * page; backend PUT /templates/:eventKey is ADMIN).
 *
 * Left: the template (subject, message, variable chips, on/off, save), and
 * a test send of the unsaved text with sample data (POST
 * /templates/:eventKey/test, ADMIN; ignores the on/off switches).
 * Right (sticky on large screens, below the editor on small ones): a live
 * preview of the real HTML email, rendered by the server with sample data
 * from the unsaved text and shown in a sandboxed iframe (no scripts). The
 * message text is placed inside a fixed email layout; it is never raw HTML.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { createAPIClient, INotificationsApi } from '../../api';
import * as types from '../../api/types';
import { getErrorMessage } from '../../utils/apiError';
import { inputClass, labelClass, primaryButtonClass, secondaryButtonClass } from '../../components/formStyles';
import { TemplateStatusBadge } from '../../components/NotificationBadges';
import { Toast } from '../../components/Toast';
import { useAuth } from '../../context/AuthContext';
import { insertAtCursor, messageStatusText, NOTIFICATION_SETTINGS_PATH, variableToken } from './notificationUi';

/** Wait after the last keystroke before asking the server for a new preview. */
export const PREVIEW_DEBOUNCE_MS = 350;

export function NotificationTemplatePage() {
  const { eventKey = '' } = useParams();
  const api: INotificationsApi = useMemo(() => createAPIClient(), []);
  const [template, setTemplate] = useState<types.NotificationTemplate | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'missing' | 'error'>('loading');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setState('loading');
    setTemplate(null);
    api
      .listNotificationTemplates()
      .then((list) => {
        if (!active) return;
        const found = list.find((t) => t.eventKey === eventKey) ?? null;
        setTemplate(found);
        setState(found ? 'ready' : 'missing');
      })
      .catch((cause) => {
        if (!active) return;
        setError(getErrorMessage(cause, 'Could not load this message'));
        setState('error');
      });
    return () => {
      active = false;
    };
  }, [api, eventKey]);

  const backLink = (
    <Link to={NOTIFICATION_SETTINGS_PATH} className="text-sm text-blue-700 hover:underline">← Notifications</Link>
  );

  if (state === 'loading') {
    return <main className="max-w-7xl mx-auto px-4 py-6 sm:py-8">Loading message…</main>;
  }

  if (state !== 'ready' || !template) {
    return (
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8 space-y-4">
        {backLink}
        {state === 'error' && error && <Toast kind="error">{error}</Toast>}
        <section className="bg-white rounded-lg shadow p-4 sm:p-5">
          <h1 className="text-xl font-bold text-gray-900">{state === 'missing' ? 'Message not found' : 'Message unavailable'}</h1>
          <p className="mt-1 text-sm text-gray-600">
            {state === 'missing'
              ? 'There is no message type with this name. Pick one from the list in Settings > Notifications.'
              : 'This message could not be loaded. Try again in a moment.'}
          </p>
        </section>
      </main>
    );
  }

  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8 space-y-6">
      <div>
        {backLink}
        <div className="mt-1 flex flex-wrap items-center gap-3">
          <h1 className="text-2xl font-bold text-gray-900">{template.label}</h1>
          <TemplateStatusBadge active={template.isActive} />
        </div>
        <p className="mt-1 text-sm text-gray-600">{template.description}</p>
      </div>
      <TemplateEditor key={template.eventKey} api={api} template={template} onSaved={setTemplate} />
    </main>
  );
}

function TemplateEditor({
  api,
  template,
  onSaved,
}: {
  api: INotificationsApi;
  template: types.NotificationTemplate;
  onSaved: (template: types.NotificationTemplate) => void;
}) {
  const [subject, setSubject] = useState(template.subject ?? '');
  const [body, setBody] = useState(template.body);
  const [preview, setPreview] = useState<types.NotificationTemplatePreview | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const { user } = useAuth();
  const [testAddress, setTestAddress] = useState(user?.email ?? '');
  const [testBusy, setTestBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const bodyRef = useRef<HTMLTextAreaElement>(null);
  const pendingCursor = useRef<number | null>(null);
  const dirty = subject !== (template.subject ?? '') || body !== template.body;
  const idBase = `template-${template.eventKey}`;

  // Live preview of the unsaved text, debounced. The last good preview stays
  // on screen while a new one loads or when the text is invalid.
  useEffect(() => {
    let active = true;
    setPreviewLoading(true);
    const timer = window.setTimeout(() => {
      api
        .previewNotificationTemplate(template.eventKey, { subject, body })
        .then((p) => {
          if (!active) return;
          setPreview(p);
          setPreviewError(null);
        })
        .catch((cause) => {
          if (active) setPreviewError(getErrorMessage(cause, 'Preview failed'));
        })
        .finally(() => {
          if (active) setPreviewLoading(false);
        });
    }, PREVIEW_DEBOUNCE_MS);
    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [api, template.eventKey, subject, body]);

  // Put the cursor after an inserted variable
  useEffect(() => {
    if (pendingCursor.current === null || !bodyRef.current) return;
    const cursor = pendingCursor.current;
    pendingCursor.current = null;
    bodyRef.current.focus();
    bodyRef.current.setSelectionRange(cursor, cursor);
  }, [body]);

  const insertVariable = useCallback((name: string) => {
    const element = bodyRef.current;
    setBody((current) => {
      const next = insertAtCursor(current, variableToken(name), element?.selectionStart ?? null, element?.selectionEnd ?? null);
      pendingCursor.current = next.cursor;
      return next.value;
    });
  }, []);

  async function save(data: types.UpdateNotificationTemplateRequest, message: string) {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const saved = await api.updateNotificationTemplate(template.eventKey, data);
      onSaved(saved);
      setNotice(message);
    } catch (cause) {
      setError(getErrorMessage(cause, 'Could not save the message'));
    } finally {
      setBusy(false);
    }
  }

  /** Email the current (unsaved) subject and body, rendered with sample data. */
  async function sendTest() {
    setTestBusy(true);
    setError(null);
    setNotice(null);
    const requested = testAddress.trim();
    try {
      const result = await api.sendTestNotificationTemplate(template.eventKey, {
        subject,
        body,
        ...(requested ? { to: requested } : {}),
      });
      const to = result.message.toAddress ?? (requested || 'your address');
      setNotice(
        result.deduplicated
          ? `A test message to ${to} was already queued in the last minute.`
          : `Test email to ${to}: ${messageStatusText(result.message)}.`
      );
    } catch (cause) {
      setError(getErrorMessage(cause, 'Could not send the test email'));
    } finally {
      setTestBusy(false);
    }
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
      {error && <Toast kind="error">{error}</Toast>}
      {notice && <Toast kind="success">{notice}</Toast>}
      <section className="bg-white rounded-lg shadow p-4 sm:p-5 min-w-0" aria-labelledby={`${idBase}-settings`}>
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h2 id={`${idBase}-settings`} className="font-semibold text-lg">Message</h2>
          <label className="flex items-center gap-2 min-h-[44px] sm:min-h-0 text-sm font-medium text-gray-800">
            <input
              type="checkbox"
              checked={template.isActive}
              disabled={busy || testBusy}
              onChange={(e) =>
                void save({ isActive: e.target.checked }, e.target.checked ? `${template.label} turned on.` : `${template.label} turned off.`)
              }
            />
            Send this message
          </label>
        </div>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void save({ subject, body }, 'Message saved.');
          }}
          className="space-y-4"
        >
          <div>
            <label htmlFor={`${idBase}-subject`} className={labelClass}>Subject</label>
            <input
              id={`${idBase}-subject`}
              className={inputClass}
              value={subject}
              maxLength={500}
              disabled={busy}
              onChange={(e) => setSubject(e.target.value.replace(/[\r\n]/g, ' '))}
            />
          </div>
          <div>
            <label htmlFor={`${idBase}-body`} className={labelClass}>Message</label>
            <textarea
              id={`${idBase}-body`}
              ref={bodyRef}
              className={`${inputClass} font-mono text-base sm:text-sm`}
              rows={14}
              value={body}
              disabled={busy}
              onChange={(e) => setBody(e.target.value)}
            />
          </div>
          <div>
            <p className={labelClass} id={`${idBase}-variables`}>Insert a variable</p>
            <div className="flex flex-wrap gap-2 sm:gap-1" role="group" aria-labelledby={`${idBase}-variables`}>
              {template.variables.map((v) => (
                <button
                  key={v.name}
                  type="button"
                  title={v.description}
                  disabled={busy}
                  className="rounded border border-gray-300 bg-gray-50 px-2 py-1.5 sm:py-0.5 text-xs font-mono break-all text-gray-700 hover:bg-gray-100"
                  onClick={() => insertVariable(v.name)}
                >
                  {variableToken(v.name)}
                </button>
              ))}
            </div>
          </div>
          <div className="rounded bg-blue-50 p-3 text-sm text-blue-900" role="note">
            <p className="font-medium">How the email looks</p>
            <p className="mt-1">
              Your message is placed inside a fixed email layout with your company name at the top. Write plain text:
              a blank line starts a new paragraph, links are made clickable, and a line with just a link (or
              &ldquo;Label: link&rdquo;) becomes a button. Variables are filled in when the message is sent, and any HTML is shown
              as text.
            </p>
          </div>
          <button type="submit" className={`${primaryButtonClass} w-full sm:w-auto`} disabled={busy || testBusy || !dirty}>
            Save message
          </button>
        </form>
        <form
          className="mt-5 pt-4 border-t border-gray-200 flex flex-col sm:flex-row sm:items-end gap-3"
          aria-label="Send a test email"
          onSubmit={(e) => {
            e.preventDefault();
            void sendTest();
          }}
        >
          <div className="flex-1 min-w-0">
            <label htmlFor={`${idBase}-test-to`} className={labelClass}>Send a test email to</label>
            <input
              id={`${idBase}-test-to`}
              type="email"
              className={inputClass}
              value={testAddress}
              maxLength={320}
              autoComplete="off"
              placeholder="name@example.com"
              disabled={testBusy}
              onChange={(e) => setTestAddress(e.target.value)}
            />
            <p className="mt-1 text-xs text-gray-500">
              Sends the text above as it is now, including unsaved changes, filled in with sample data. The subject
              starts with &ldquo;[Test]&rdquo;. Leave blank to send to your own address.
            </p>
          </div>
          <button type="submit" className={`${secondaryButtonClass} w-full sm:w-auto`} disabled={busy || testBusy}>
            {testBusy ? 'Sending…' : 'Send test email'}
          </button>
        </form>
      </section>

      <aside
        className="bg-white rounded-lg shadow p-4 sm:p-5 min-w-0 lg:sticky lg:top-4 self-start"
        aria-label={`${template.label} preview`}
      >
        <div className="flex items-center justify-between gap-3 mb-3">
          <h2 className="font-semibold text-lg">Preview</h2>
          <span className="text-xs text-gray-500" aria-live="polite">
            {previewLoading ? 'Updating…' : 'Sample data'}
          </span>
        </div>
        {previewError && (
          <p role="alert" className="mb-3 rounded border border-red-200 bg-red-50 p-2 text-sm text-red-800 break-words">
            {previewError}
            {preview ? ' Showing the last valid preview.' : ''}
          </p>
        )}
        {preview ? (
          <>
            {preview.subject !== null && (
              <div className="mb-3 rounded border border-gray-200 bg-gray-50 px-3 py-2 text-sm min-w-0">
                <span className="text-gray-500">Subject: </span>
                <span className="font-semibold text-gray-900 break-words" data-testid="preview-subject">{preview.subject}</span>
              </div>
            )}
            {preview.html ? (
              <iframe
                title={`${template.label} email preview`}
                sandbox=""
                srcDoc={preview.html}
                className="block w-full h-[480px] lg:h-[calc(100vh-14rem)] min-h-[360px] rounded border border-gray-200 bg-gray-100"
              />
            ) : (
              <pre className="whitespace-pre-wrap break-words font-sans text-sm text-gray-800">{preview.body}</pre>
            )}
            {preview.html && (
              <details className="mt-3 text-sm">
                <summary className="cursor-pointer text-gray-600">Plain-text version</summary>
                <pre className="mt-2 whitespace-pre-wrap break-words rounded bg-gray-50 p-3 font-sans text-gray-800">{preview.body}</pre>
              </details>
            )}
          </>
        ) : previewError ? (
          <p className="text-sm text-gray-600">Preview unavailable.</p>
        ) : (
          <p className="text-sm text-gray-500">Rendering…</p>
        )}
      </aside>
    </div>
  );
}
